import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { Combobox } from 'baseui/combobox';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input } from 'baseui/input';
import { SIZE } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { Textarea } from 'baseui/textarea';
import { toaster } from 'baseui/toast';
import { TreeLabelInteractable, TreeView } from 'baseui/tree-view';
import { HeadingMedium, HeadingSmall } from 'baseui/typography';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { ButtonRow, SelectableUserPill, UserIcon, UserPill } from '@gtintel/platform-atoms';
import { NPRequestStatus, ProposedPartnerPlacement } from '@gtintel/platform-types';
import { COLOURS, getMargin, useAsyncOp, useDebounce } from '@gtintel/platform-utils';
import PropertiesCard from '../../../components/AssignmentEditorPropertiesCard';
import DocumentsFlatList from '../../../components/Documents/DocumentsFlatList';
import { ScrollResistantNumberInput } from '../../../components/Inputs';
import RequestStatusIcon from '../../../components/RequestStatusIcon';
import { ProjectContext, RequestEditorContext, RequestsContext } from '../../context';
import { SelectionState } from '../../context/types';
import { NPRequestStatusBackground, NPRequestStatusColor } from '../utils';
import { AssignmentInfoCards } from './AssignmentInfoCards';
import { MarginTargets } from './MarginTargets';

// ------------------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------------------

const parsePrice = price => {
  return isFinite(+price) ? +price : 0;
};
const compareTreeToRequestComponent = (component, tree) => {
  return component.jurisdiction_id === String(tree.data.selector.jurisdiction_id) && component.scope_component_id === tree.data.selector.scope_component_id && component.subject_id === tree.data.selector.subject_id;
};

// ------------------------------------------------------------------------------------

export const Send = () => {
  const ctx = React.useContext(RequestEditorContext);
  const tree = ctx.selectedScope(ctx.report(false));
  const [css] = useStyletron();
  return <HeadingLevel>
      {tree ? <ReportTree node={tree} /> : <Block>No scope has been selected for this assignment yet.</Block>}
      {/* DRAFT assignments created by the GTI Hub dispatch already carry
          their documents (linked at create time) — show them here so the
          files are visible before the assignment is started, not only on
          the Assign/Finished steps. */}
      <div className={css({
      marginTop: '28px',
      marginBottom: '28px'
    })}>
        <Heading>Assignment Documents</Heading>
        <DocumentsFlatList canDelete={true} canUpload={true} isLeadPartnerAssignment={false} />
      </div>
    </HeadingLevel>;
};
export const Wait = () => {
  const ctx = React.useContext(RequestEditorContext);
  const tree = ctx.selectedScope(ctx.report(true));
  const [css] = useStyletron();
  let msg;
  if (ctx.status() === NPRequestStatus.Cancelled) {
    msg = 'The request as been cancelled';
  } else if (ctx.partner()) {
    let statusString = `${ctx.partner().full_name ? ctx.partner().full_name : ctx.partner().email} `;
    switch (ctx.status()) {
      case NPRequestStatus.Sent:
        statusString += 'has not responded to this request.';
        break;
      case NPRequestStatus.Accepted:
        statusString += 'has accepted this request.';
        break;
      case NPRequestStatus.Declined:
        statusString += 'has declined this request.';
        break;
    }
    msg = <>
        <UserIcon user={ctx.partner()} />
        <span>{statusString}</span>
      </>;
  }
  return <React.Fragment>
      <div className={css({
      alignItems: 'center',
      background: NPRequestStatusBackground[ctx.status()],
      border: `2px solid ${NPRequestStatusColor[ctx.status()]}`,
      borderRadius: '4px',
      color: NPRequestStatusColor[ctx.status()],
      display: 'flex',
      flexFlow: 'row nowrap',
      gap: '8px',
      marginBottom: '16px',
      padding: '14px'
    })} data-testid="wait-step">
        {msg}
      </div>

      <PropertiesCard />

      <div className={css({
      marginBottom: '28px'
    })}>
        <HeadingLevel>
          <Heading>Partner</Heading>
          <UserPill user={ctx.partner()} showName />
        </HeadingLevel>
      </div>

      <LockableRequestEditFields />

      <HeadingLevel>
        <div className={css({
        margin: '28px 0'
      })}>
          <Heading>Documents</Heading>
          <DocumentsFlatList canDelete={true} canUpload={true} isLeadPartnerAssignment={false} />
        </div>

        {tree && <ReportTree node={tree} />}
      </HeadingLevel>
    </React.Fragment>;
};
export const Assign = () => {
  const ctx = React.useContext(RequestEditorContext);
  const tree = ctx.selectedScope(ctx.report(true));
  const [css] = useStyletron();
  return <HeadingLevel>
      <PropertiesCard />
      <div className={css({
      marginBottom: '28px'
    })}>
        <HeadingLevel>
          <Heading>Partner</Heading>
          <UserPill user={ctx.partner()} showName />
        </HeadingLevel>
      </div>
      <LockableRequestEditFields />
      <div className={css({
      marginBottom: '28px'
    })}>
        <Heading>Assignment Documents</Heading>
        <DocumentsFlatList canDelete={true} canUpload={true} isLeadPartnerAssignment={false} />
      </div>
      {tree && <ReportTree node={tree} />}
    </HeadingLevel>;
};
export const Finished = () => {
  const ctx = React.useContext(RequestEditorContext);
  const tree = ctx.selectedScope(ctx.report(true));
  const [css] = useStyletron();
  return <React.Fragment>
      <Heading>Assignment finished</Heading>

      <PropertiesCard />

      <div className={css({
      marginBottom: '28px'
    })}>
        <HeadingLevel>
          <Heading>Partner</Heading>
          <UserPill user={ctx.partner()} showName />
        </HeadingLevel>
      </div>

      <LockableRequestEditFields />

      <HeadingLevel>
        <div className={css({
        margin: '28px 0'
      })}>
          <Heading>Documents</Heading>
          <DocumentsFlatList canDelete={true} canUpload={true} isLeadPartnerAssignment={false} />
        </div>
        {[NPRequestStatus.Submitted, NPRequestStatus.Finished].includes(ctx.status()) && ctx.investigatorNotes() && <div className={css({
        margin: '28px 0'
      })}>
              <HeadingLevel>
                <Heading>Investigator Notes</Heading>
                <FormControl>
                  <Textarea value={ctx.investigatorNotes()} readOnly overrides={{
              Input: {
                style: {
                  minHeight: '120px'
                }
              }
            }} />
                </FormControl>
              </HeadingLevel>
            </div>}
        {tree && <ReportTree node={tree} />}
      </HeadingLevel>
    </React.Fragment>;
};
export const Partner = ({
  editable
}) => {
  const [options, setOptions] = React.useState([]);
  const [q, setQ] = React.useState('');
  const ctx = React.useContext(RequestEditorContext);
  const [toggleSearch, setToggleSearch] = useState(ctx.proposedPartners().length === 0);
  const {
    fn: searchPartners
  } = useAsyncOp(ctx.searchPartners);
  const {
    isLoading: isSettingPartner,
    fn: setPartner
  } = useAsyncOp(ctx.setPartner);
  const tree = ctx.selectedScope(ctx.report(true));
  const proposedPartners = ctx.proposedPartners().sort((a, b) => a.placement - b.placement);
  const firstChoices = [];
  const secondChoices = [];
  const testChoices = [];
  proposedPartners.forEach(partner => {
    if (partner.placement === ProposedPartnerPlacement.FIRST) {
      firstChoices.push(partner);
    } else if (partner.placement === ProposedPartnerPlacement.SECOND) {
      secondChoices.push(partner);
    } else {
      testChoices.push(partner);
    }
  });
  const [css] = useStyletron();
  const partnerSelected = partner => {
    if (!ctx.partner()) return false;
    return String(ctx.partner().id) === String(partner.partner.id);
  };
  const onChange = (nextQ, option) => {
    if (option) {
      setPartner(option);
    } else {
      setPartner(null);
      setQ('');
    }
    setQ(nextQ);
  };
  const search = React.useCallback(_.debounce(async query => {
    const matches = await searchPartners(query, ctx.isQaAssignment());
    setOptions(matches);
  }, 500), [searchPartners, setOptions]);
  const createPartnerChoicePills = partners => {
    return partners.length > 0 ? <div className={css({
      display: 'flex',
      flexFlow: 'row wrap',
      gap: '8px',
      marginBottom: '16px',
      width: 'fit-content'
    })}>
        {partners.map(partner => {
        return <SelectableUserPill selected={partnerSelected(partner)} onClick={setPartner} user={partner.partner} showName title={`Email: ${partner.partner.email}`} tagLine={partner.justification} />;
      })}
      </div> : <div className={css({
      alignItems: 'center',
      background: '#fff',
      border: '2px solid transparent',
      borderRadius: '28px',
      display: 'flex',
      flexFlow: 'row nowrap',
      marginBottom: '16px',
      padding: '8px 12px',
      width: 'fit-content'
    })}>
        <Heading styleLevel={5} padding={0} margin={0}>
          No matching partners found
        </Heading>
      </div>;
  };
  React.useEffect(() => {
    search(q);
  }, [q]);
  return <React.Fragment>
      {proposedPartners.length > 0 && !toggleSearch && <div data-testid="proposed-partners" className={css({
      display: 'flex',
      flexFlow: 'column',
      margin: '28px 0'
    })}>
          <HeadingLevel>
            <Heading>First Choices</Heading>
            {createPartnerChoicePills(firstChoices)}
          </HeadingLevel>

          <HeadingLevel>
            <Heading>Second Choices</Heading>
            {createPartnerChoicePills(secondChoices)}
          </HeadingLevel>

          <HeadingLevel>
            <Heading>Test Choices</Heading>
            {createPartnerChoicePills(testChoices)}
          </HeadingLevel>
        </div>}

      {(proposedPartners.length === 0 || toggleSearch) && <div className={css({
      margin: '14px 0'
    })}>
          <span className={css({
        margin: '8px 4px'
      })}>
            enter 3 or more characters to search
          </span>
          <Combobox value={q || ctx.partner()?.full_name} options={options} size={SIZE.large} onChange={onChange} listBoxLabel="name" mapOptionToString={o => o.full_name} overrides={{
        Root: {
          props: {
            'data-testid': 'partner-select'
          }
        }
      }} />
        </div>}

      <ButtonRow flexPosition="flex-end">
        <Button kind="primary" onClick={() => setToggleSearch(!toggleSearch)}>
          {toggleSearch ? 'Show proposed partners' : 'I need a different partner'}
        </Button>
      </ButtonRow>

      <HeadingLevel>
        <div className={css({
        background: '#fff',
        border: `2px solid ${COLOURS.borderGray}`,
        borderRadius: '8px',
        display: 'flex',
        flexFlow: 'column wrap',
        gap: '8px',
        margin: '62px 0',
        padding: '28px'
      })}>
          {editable && <RequestEditFields showPartner />}
        </div>
        {tree && <ReportTree node={tree} />}
      </HeadingLevel>
    </React.Fragment>;
};
export const ScopeSelection = ({
  readonly
}) => {
  const ctx = React.useContext(RequestEditorContext);
  const requestsCtx = React.useContext(RequestsContext);
  const projectCtx = React.useContext(ProjectContext);
  const [css] = useStyletron();
  const [folded, setFolded] = React.useState({});
  const root = ctx.report(readonly);
  const [isQa, setIsQa] = useState(ctx.isQaAssignment());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const toBasewebTree = ns => {
    return ns.map(n => ({
      info: n.data,
      id: n.path,
      label: () => {
        const requestComponents = [];
        requestsCtx.list().filter(request => request.components.filter(component => {
          return compareTreeToRequestComponent(component, n) ? requestComponents.push({
            component,
            partner: request.partner,
            status: request.status
          }) : false;
        }));
        return <Selectable label={n.name} readonly={readonly} state={ctx.selectionState(n)} onToggle={() => ctx.toggleSelected(n)} disabled={n.data.disabled} components={requestComponents} hasChildren={n.children.length > 0} />;
      },
      isExpanded: !folded[n.path],
      children: toBasewebTree(n.children)
    }));
  };
  const onToggleExpand = n => {
    setFolded(f => ({
      ...f,
      [n.id]: !f[n.id]
    }));
  };
  const getJurisdictions = root => {
    const jurisdictions = new Map();
    const traverse = node => {
      const {
        jurisdiction_id
      } = node.data.selector;
      if (jurisdiction_id && !jurisdictions.has(jurisdiction_id)) {
        jurisdictions.set(jurisdiction_id, {
          name: node.name,
          jurisdiction_id
        });
      } else {
        node.children.forEach(traverse);
      }
    };
    traverse(root);
    return Array.from(jurisdictions.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  const onToggleJurisdiction = (jurisdiction_id, targetState) => {
    const traverse = node => {
      if (node.data.selector.jurisdiction_id === jurisdiction_id && ctx.selectionState(node) !== targetState) {
        ctx.toggleSelected(node);
      } else {
        node.children.forEach(traverse);
      }
    };
    traverse(root);
  };
  const getJurisdictionState = jurisdiction_id => {
    const states = new Set();
    const traverse = node => {
      if (node.data.selector.jurisdiction_id === jurisdiction_id) {
        states.add(ctx.selectionState(node));
      } else {
        node.children.forEach(traverse);
      }
    };
    traverse(root);
    if (states.size === 0) return SelectionState.Empty; // No matches
    if (states.size === 1) return states.values().next().value; // All states are the same
    return SelectionState.Partial; // Mix of states
  };
  const jurisdictions = React.useMemo(() => getJurisdictions(root), [root]);
  const data = React.useMemo(() => toBasewebTree([root]), [root]);
  const setIsQAAssignment = value => {
    setIsLoading(true);
    setLoadingText('Setting QA Assignment');
    // Select ALL scope elements from the tree and make them disabled
    const traverse = node => {
      if (!node.data.disabled) {
        node.data.disabled = true;
      }
      node.children.forEach(traverse);
    };
    traverse(root);
    ctx.toggleSelected(root).then(() => {
      ctx.setIsQaAssignment(value).then(() => {
        setIsQa(value);
      }).catch(err => {
        console.error('Error setting is_qa_assignment: ', err);
        toaster.negative('Failed to set as QA Assignment');
      });
    }).catch(err => {
      console.error('Error adding all scope elements: ', err);
      toaster.negative('Failed to set as QA Assignment');
    });
    setLoadingText('Finished!');
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };
  return <React.Fragment>
      {isLoading && <div className={css({
      alignItems: 'center',
      background: 'rgba(255,255,255,0.6)',
      bottom: '0',
      display: 'flex',
      flexFlow: 'column nowrap',
      height: '100vh',
      justifyContent: 'center',
      left: '0',
      position: 'fixed',
      top: '0',
      width: '100vw',
      zIndex: '100'
    })}>
          <Spinner />
          &nbsp;
          <HeadingLevel>
            <Heading>{loadingText ?? 'Loading...'}</Heading>
          </HeadingLevel>
        </div>}
      <div className={css({
      marginBottom: '28px'
    })}>
        <Checkbox checked={isQa} disabled={!projectCtx.qaRequired() || !!readonly || isLoading} onChange={e => {
        setIsQAAssignment(e.target.checked);
      }} title="Setting this adds all scope items but only for display purposes.">
          {projectCtx.hasQaAssignment() ? 'There is already a QA Assignment' : 'Is this a QA Assignment?'}
        </Checkbox>
      </div>
      {jurisdictions && <div className={css({
      paddingBottom: '1rem'
    })}>
          <HeadingMedium>Jurisdictions</HeadingMedium>
          {jurisdictions.map(jurisdiction => {
        const jurisdictionState = getJurisdictionState(jurisdiction.jurisdiction_id);
        return <Checkbox key={jurisdiction.jurisdiction_id} disabled={!!readonly} checked={jurisdictionState === SelectionState.Full} isIndeterminate={jurisdictionState === SelectionState.Partial} onChange={() => {
          const newState = jurisdictionState === SelectionState.Full ? SelectionState.Empty : SelectionState.Full;
          onToggleJurisdiction(jurisdiction.jurisdiction_id, newState);
        }} overrides={{
          Root: {
            props: {
              'data-testid': `jurisdiction-checkbox-${jurisdiction.jurisdiction_id}`
            }
          }
        }}>
                {jurisdiction.name}
              </Checkbox>;
      })}
        </div>}

      <HeadingMedium>Scope Outline</HeadingMedium>
      <TreeView data={data} onToggle={onToggleExpand} indentGuides overrides={{
      Root: {
        props: {
          'data-testid': 'scope-tree-view'
        }
      }
    }} />
    </React.Fragment>;
};
export const ReportTree = ({
  node
}) => {
  const [css] = useStyletron();
  return <div data-testid="report-tree" className={css({
    marginLeft: '1.0em'
  })}>
      <Heading>{node.name}</Heading>
      <div dangerouslySetInnerHTML={{
      __html: node.data.instructionsHtml
    }}></div>
      <HeadingLevel>
        {node.children.map(c => <ReportTree key={c.path} node={c} />)}
      </HeadingLevel>
    </div>;
};
const RequestEditFields = ({
  editable,
  showPartner
}) => {
  const ctx = React.useContext(RequestEditorContext);
  const {
    assignmentCosts,
    clientAdjustmentsCost,
    netPrice,
    currency,
    edit,
    partnerPrice,
    partnerAdjustmentsCost,
    qaCost,
    isManagedByHub
  } = React.useContext(ProjectContext);
  const [fee, setFee] = useState(ctx.fee());
  // GTI Hub projects: the partner fee / due date are agreed and edited in
  // the Hub and sync here.
  const managedByHub = isManagedByHub();
  const [projectMargin, setProjectMargin] = useState(1);
  const marginFee = ctx.status() === NPRequestStatus.Draft ? 0 : ctx.fee();
  const adjustedAssignmentCost = assignmentCosts() - marginFee;
  useEffect(() => {
    setProjectMargin(getMargin(parsePrice(partnerPrice()) + adjustedAssignmentCost + parsePrice(qaCost()) + fee + partnerAdjustmentsCost(), netPrice() + clientAdjustmentsCost()));
  }, [netPrice, fee, partnerPrice, adjustedAssignmentCost]);
  const debouncedSetDueDate = useDebounce(ctx.setDueDate, 500);
  const debouncedSetFee = useDebounce(ctx.setFee, 500);
  const debouncedEdit = useDebounce(edit, 500);
  const handlePriceChange = (setValue, value) => {
    setValue(parsePrice(value));
  };
  const handlePriceBlur = (key, value) => {
    debouncedSetFee(parsePrice(value));
  };
  const handleDueDateChange = value => {
    debouncedSetDueDate(value);
  };
  return <FlexGrid flexDirection="row" width="100%">
      <FlexGridItem>
        <AssignmentInfoCards projectMargin={projectMargin} />
        {ctx.status() === NPRequestStatus.Draft && <MarginTargets projectMargin={projectMargin} />}
      </FlexGridItem>
      <FlexGridItem>
        {managedByHub && <p data-testid="hub-managed-request-notice">
            Fee and due date are managed in GTI Hub for this project.
          </p>}
        <FlexGrid flexGridColumnCount={3} flexGridColumnGap="scale400" marginBottom="scale800">
          <FlexGridItem>
            <HeadingSmall margin="0" marginRight="scale300">
              Fee
            </HeadingSmall>
            <ScrollResistantNumberInput id="request_fee" min={0} disabled={!!editable || managedByHub || ctx.status() === NPRequestStatus.Finished} value={fee} startEnhancer={currency() ?? '$'} onChange={e => handlePriceChange(setFee, e.target.value)} onBlur={e => handlePriceBlur('fee', e.target.value)} />
          </FlexGridItem>

          <FlexGridItem>
            <HeadingSmall margin="0" marginRight="scale300">
              Due Date
            </HeadingSmall>
            <Input disabled={!!editable || managedByHub} overrides={{
            Root: {
              props: {
                'data-testid': 'assignment-due-date'
              }
            }
          }} type="date" value={ctx.dueDate() ?? null} onChange={e => {
            handleDueDateChange(e.currentTarget.value);
            if (ctx.isQaAssignment()) {
              debouncedEdit({
                np_completion_date: e.currentTarget.value
              });
            }
          }} />
          </FlexGridItem>

          <FlexGridItem>
            {ctx.partner() && showPartner && <div>
                <HeadingSmall margin="0" marginRight="scale300">
                  Partner
                </HeadingSmall>
                <UserPill user={ctx.partner()} hasBorder showName />
              </div>}
          </FlexGridItem>
        </FlexGrid>
      </FlexGridItem>
    </FlexGrid>;
};
const Selectable = ({
  label,
  onToggle,
  state,
  readonly,
  disabled,
  hasChildren,
  components
}) => {
  const [css, theme] = useStyletron();
  const pill = {
    alignItems: 'center',
    background: theme.colors.backgroundPrimary,
    border: '1px solid #e2e2e2',
    borderRadius: '28px',
    display: 'flex',
    flexFlow: 'row nowrap',
    gap: '8px',
    justifyContent: 'center',
    padding: '0 8px'
  };
  return <TreeLabelInteractable>
      {readonly ? label : <div className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      gap: '8px',
      justifyContent: 'space-between'
    })}>
          <Checkbox isIndeterminate={state === SelectionState.Partial} checked={state === SelectionState.Full} onChange={onToggle} disabled={disabled}>
            {label}
          </Checkbox>
          {components.filter(c => c.partner).length > 0 && !hasChildren && !disabled ? <div className={css({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row nowrap',
        fontSize: '.75rem',
        marginLeft: '16px'
      })}>
              {components.filter(c => c.partner).map(c => <div key={nanoid()} className={css(pill)} title={`${c.partner.full_name} (${c.partner.email}) - ${NPRequestStatus[c.status]}`}>
                    <p className={css({
            margin: '0',
            padding: '0'
          })}>
                      {c.partner.full_name}
                    </p>
                    <RequestStatusIcon status={c.status} />
                  </div>)}
            </div> : components.filter(c => !c.partner).length > 0 ? <div className={css(pill)}>
              <small>Created but not assigned</small>
            </div> : !hasChildren && <div className={css(pill)}>
                <small>Not yet requested</small>
              </div>}
        </div>}
    </TreeLabelInteractable>;
};
const LockableRequestEditFields = ({
  showPartner
}) => {
  const [isLocked, setIsLocked] = React.useState(true);
  const [css] = useStyletron();
  return <div className={css({
    backgroundColor: '#fff',
    border: `2px solid ${COLOURS.borderGray}`,
    borderRadius: '14px',
    display: 'flex',
    flexFlow: 'row wrap',
    marginBottom: '24px',
    padding: '14px',
    position: 'relative'
  })}>
      <Button size="compact" className={css({
      position: 'absolute',
      right: '14px',
      top: '14px'
    })} onClick={() => setIsLocked(!isLocked)}>
        <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} size="lg" />
      </Button>
      <div className={css({
      display: 'flex',
      flexFlow: 'row wrap',
      gap: '14px',
      marginBottom: '24px'
    })}>
        <RequestEditFields editable={isLocked} showPartner={showPartner} />
      </div>
    </div>;
};